/**
 * **Whether a goal's bar is empty because nothing was measured**, and which reading would fill it.
 *
 * Promoted out of the client detail's summary column on 26 September 2026, when the goal task card
 * became its second reader: both say "Brak pomiaru" and point at the same tab, and two copies of
 * the rule would part on the one type that matters (`Custom`).
 *
 * ── The backend sends 0 for both, and they are not the same fact ──────────────────────────────
 * `CustomerGoalProcessor` returns `(null, 0)` for a `WeightTarget` whose client has never been
 * weighed, and `ComputePrBasedPct` reads a null current record as a 0 origin, so `pct` is 0 there
 * too. `CurrentValue` is what tells a missing reading from a real zero: it is null exactly when
 * there is no reading.
 *
 * ── `Custom` is NOT a gap ─────────────────────────────────────────────────────────────────────
 * A `Custom` goal has `CurrentValue: null` by design — it has no metric — and its `pct` is the
 * elapsed fraction of `CreatedAt → Deadline`. That bar measures TIME and is always meaningful.
 * `SessionCount` is never a gap either: zero sessions is a real, measured zero.
 */

export type GoalType = 'PrBased' | 'SessionCount' | 'WeightTarget' | 'Custom';

const GOAL_TYPES: readonly GoalType[] = ['PrBased', 'SessionCount', 'WeightTarget', 'Custom'];

export function goalTypeOf(wire: string | null | undefined): GoalType | null {
  return GOAL_TYPES.find((type) => type === wire) ?? null;
}

/** The client detail tab that produces the reading a measurable goal type is scored on. */
export type GoalReadingTab = 'body' | 'records';

const MEASURED_BY: Readonly<Partial<Record<GoalType, GoalReadingTab>>> = {
  // `CurrentValue` is the best record on the goal's exercise, so the Rekordy tab is where one
  // appears — not Pomiary, which knows nothing about a lift.
  PrBased: 'records',
  // `CurrentValue` is the latest body weight.
  WeightTarget: 'body',
};

/** The tab that would produce the missing reading, or `null` where the bar already means something. */
export function goalMeasurementTab(
  type: GoalType | null,
  currentValue: number | null | undefined,
): GoalReadingTab | null {
  if (type === null || currentValue != null) return null;
  return MEASURED_BY[type] ?? null;
}
